import React, { useEffect, useState } from 'react';
import { Grid, InputAdornment, TextField, TooltipProps, Tooltip, Theme, Icon } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { add, subtract } from 'lodash';
import {
  ContractChargeAmount,
  ContractRegularRate,
  ContractShift,
  RegularRate,
  chargePremium,
  onCallRate,
  preceptorRate,
} from 'app/models/Placement/PlacementMods';
import { getCalculatedAmount } from './BillOvertime';
import { validDecimal } from 'app/helpers/numberHelper';
import { makeStyles } from 'tss-react/mui';
import { getInvalidAmount } from './BillOrientation';
import { ChargeRateTooltip } from '../../../CommonComponents/ChargeRateTooltip';

export const PreceptorSection = ({ classes, isModEdit }: { classes: any; isModEdit: boolean }) => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext();
  const regularRateValue = useWatch<string>({ name: RegularRate });
  const contractRegularRate = useWatch<number>({ name: ContractRegularRate });
  const contractShift = useWatch<number>({ name: ContractShift });
  const contractChargeAmount = useWatch<number>({ name: ContractChargeAmount });
  const [chargeAmountManualUpdate, setChargeAmountManualUpdate] = useState<boolean>(false);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);

  const handleRegularRateChange = (newRegularRate: string) => {
    if (contractChargeAmount > 0) {
      const calculation = add(
        subtract(contractChargeAmount, contractRegularRate),
        parseFloat(newRegularRate)
      );
      const chargeCalculation = parseFloat(calculation.toFixed(2));
      setValue(chargePremium, chargeCalculation.toFixed(2), { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    } else {
      setValue(chargePremium, '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    }
  };

  useEffect(() => {
    if (!isInitialLoad && regularRateValue) {
      handleRegularRateChange(regularRateValue);
    }
    setIsInitialLoad(false);
  }, [regularRateValue]);

  useEffect(() => {
    if (chargeAmountManualUpdate) {
      setChargeAmountManualUpdate(false);
    }
  }, [regularRateValue]);

  return (
    <Grid item className={classes.flex}>
      <Controller
        name={preceptorRate}
        control={control}
        render={({ field: { onChange, value, ...rest } }) => (
          <TextField
            value={getValues(preceptorRate)}
            className={classes.compTextfields}
            InputLabelProps={{
              shrink: getValues(preceptorRate),
            }}
            variant="filled"
            label={t('placement.placementMods.addPreceptorRate')}
            helperText={regularRateValue ? getCalculatedAmount(getValues, preceptorRate) : getInvalidAmount()}
            onChange={e => {
              if (validDecimal(e.target.value, { numberLength: 6, decimalLength: 2 })) {
                setValue(preceptorRate, e.target.value);
                onChange(e.target.value);
              }
            }}
            InputProps={{
              readOnly: !isModEdit,
              style: {
                pointerEvents: isModEdit ? 'auto' : 'none',
              },
              startAdornment: (
                <>
                  {getValues(preceptorRate) && <InputAdornment position="start">{t('currency.dollar')}</InputAdornment>}
                </>
              ),
              sx: { fontSize: '14px' },
            }}
            {...rest}
          />
        )}
      />
      <Grid className={classes.factorMarginBillRate}>
        <Controller
          name={onCallRate}
          control={control}
          render={({ field: { onChange, value, ...rest } }) => (
            <TextField
              value={getValues(onCallRate)}
              variant="filled"
              className={classes.compTextfields}
              label={`${t(`placement.placementMods.${onCallRate}`)}`}
              InputLabelProps={{
                shrink: getValues(onCallRate) ?? false,
              }}
              onChange={e => {
                if (validDecimal(e.target.value, { numberLength: 6, decimalLength: 2 })) {
                  setValue(onCallRate, e.target.value);
                  onChange(e.target.value);
                }
              }}
              InputProps={{
                readOnly: !isModEdit,
                style: {
                  pointerEvents: isModEdit ? 'auto' : 'none',
                },
                startAdornment: (
                  <>
                    {getValues(onCallRate) && <InputAdornment position="start">{t('currency.dollar')}</InputAdornment>}
                  </>
                ),
                sx: { fontSize: '14px' },
              }}
              {...rest}
            />
          )}
        />
      </Grid>
      <Grid className={classes.factorMarginBillRate}>
        <Controller
          name={chargePremium}
          control={control}
          render={({ field: { onChange, value, ...rest } }) => (
            <TextField
              value={getValues(chargePremium)}
              variant="filled"
              className={classes.compTextfields}
              label={`${t(`placement.placementMods.${chargePremium}`)}`}
              helperText={
                <ChargeRateTooltip
                  contractRegularRate={contractRegularRate}
                  contractChargeAmount={contractChargeAmount}
                  contractShift={contractShift}
                  regularRateTitle={t('placement.placementMods.chargeRateTooltip.contractRegularRate')}
                  chargeAmountTitle={t('placement.placementMods.chargeRateTooltip.contractChargeAmount')}
                  shiftTitle={t('placement.placementMods.chargeRateTooltip.contractShift')}
                />
              }
              InputLabelProps={{
                shrink: getValues(chargePremium) ?? false,
              }}
              onChange={e => {
                if (validDecimal(e.target.value, { numberLength: 6, decimalLength: 2 })) {
                  setValue(chargePremium, e.target.value);
                  onChange(e.target.value);
                  setChargeAmountManualUpdate(true);
                }
              }}
              InputProps={{
                readOnly: !isModEdit,
                style: {
                  pointerEvents: isModEdit ? 'auto' : 'none',
                },
                startAdornment: (
                  <>
                    {getValues(chargePremium) && (
                      <InputAdornment position="start">{t('currency.dollar')}</InputAdornment>
                    )}
                  </>
                ),
                sx: { fontSize: '14px' },
              }}
              {...rest}
            />
          )}
        />
      </Grid>
    </Grid>
  );
};
const StyledTootltip = makeStyles()((theme: Theme) => ({
  arrow: {
    '&:before': {
      border: '1px solid #E3DFDF',
    },
    color: '#FFFFFF',
  },
  tooltip: {
    minWidth: '6rem',
    fontFamily: 'Roboto',
    fontSize: '12px',
    lineHeight: '18px',
    fontWeight: 'normal',
    color: '#555555',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E3DFDF',
  },
}));

export const CustomTooltip = (props: TooltipProps) => {
  const { classes } = StyledTootltip();

  return <Tooltip arrow enterDelay={500} classes={classes} {...props} />;
};

